import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { FadeUp, PageShell, Eyebrow } from "@/components/Reveal";
import { useContent } from "@/context/ContentContext";
import { usePageTitle } from "@/hooks/usePageTitle";

export default function Services() {
  usePageTitle("Services — Construction, Interiors & More | Sai Leela Decor");
  const c = useContent();
  const SERVICES = c.services;
  return (
    <PageShell testid="services-page">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-40 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <FadeUp>
              <Eyebrow className="mb-5">Services</Eyebrow>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.08] text-bone">
                Every layer, <span className="italic text-bronze">one authorship</span>
              </h1>
            </FadeUp>
          </div>
          <div className="lg:col-span-5">
            <FadeUp delay={0.15}>
              <p className="text-base font-light leading-relaxed text-stone2">
                From the first spatial sketch to the last object placed on a shelf,
                the studio authors every layer of a project. Dummy descriptions below —
                swap in your real service offering.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
          {SERVICES.map((s, i) => (
            <FadeUp key={s.num} delay={0.04 * i}>
              <div
                data-testid={`service-card-${s.num}`}
                className="group border border-white/[0.07] transition-all duration-500 hover:border-bronze/40 hover:bg-bronze/[0.03]"
              >
                {s.image && (
                  <div className="overflow-hidden">
                    <img src={s.image} alt={s.title} className="w-full h-auto transition-transform duration-700 ease-luxury group-hover:scale-105" />
                  </div>
                )}
                <div className="p-7">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-baseline gap-5">
                      <span className="font-mono text-xs text-bronze">{s.num}</span>
                      <h2 className="font-serif text-xl sm:text-2xl font-light text-bone transition-colors duration-500 group-hover:text-bronze">{s.title}</h2>
                    </div>
                    <ArrowUpRight size={18} className="text-muted2 transition-all duration-500 group-hover:text-bronze group-hover:translate-x-1 group-hover:-translate-y-1 shrink-0" />
                  </div>
                  <p className="mt-4 text-sm font-light leading-relaxed text-stone2">{s.desc}</p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {s.tags.map((t) => (
                      <span key={t} className="text-[9px] uppercase tracking-[0.25em] text-muted2 border border-white/10 px-3 py-1.5">{t}</span>
                    ))}
                  </div>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="border-t border-white/[0.07] bg-ink/40 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_100%,rgba(197,160,89,0.1),transparent_60%)]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center relative">
          <FadeUp>
            <Eyebrow className="mb-6">Not sure where to begin?</Eyebrow>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light text-bone max-w-2xl mx-auto leading-snug">
              A one-hour consultation maps your space, budget and ambitions — placeholder copy.
            </h2>
            <Link to="/contact" data-testid="services-cta-btn" className="mt-10 inline-flex items-center gap-3 bg-bronze text-obsidian text-[11px] uppercase tracking-[0.25em] px-10 py-5 transition-all duration-500 hover:bg-bronze-light">
              Request a Consultation <ArrowRight size={14} />
            </Link>
          </FadeUp>
        </div>
      </section>
    </PageShell>
  );
}
